import { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { apiPost } from '../api/client';
import { Spinner } from '../components/shared/Spinner';
import styles from './AuthCallbackPage.module.css';

type OAuthCompleteResponse = {
  readonly redirect_url: string;
};

const AuthCallbackPage = (): ReactElement => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const completeAuth = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const proxyState = params.get('proxy_state');

      if (!code) {
        setError('Missing authorization code.');
        return;
      }

      const storedState = proxyState ? sessionStorage.getItem(proxyState) : null;

      if (proxyState && storedState) {
        try {
          const result = await apiPost<OAuthCompleteResponse>('/oauth/complete', {
            code,
            state: state ?? '',
            proxy_state: proxyState,
          });
          window.location.href = result.redirect_url;
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Authentication failed.';
          setError(message);
        }
      } else {
        setError('Invalid or expired authentication state.');
      }
    };

    void completeAuth();
  }, []);

  if (error) {
    return (
      <div className={styles.page}>
        <h2 className={styles.title}>Authentication Failed</h2>
        <div className={styles.error}>{error}</div>
        <Link to="/" className={styles.homeLink}>Return to home</Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>Completing authentication...</h2>
      <Spinner />
    </div>
  );
};

export { AuthCallbackPage };
